import { CssPropertyEnumCssPropertyType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { border } from '../border/shorthandHandler';
import { corner } from '../corner/shorthandHandler';
import { spacing } from '../spacing/shorthandHandler';
import { text } from '../text/shorthandHandler';
import { SYSTEM_EMPTY, SystemEmpty } from './constants';

export type { SystemEmpty };

export interface DecomposeMapEntry {
  fn: (value: string) => Record<string, string | null>;
  nestingLevel: number;
}

export interface AtomicValueWithNesting {
  value: string;
  nestingLevel: number;
  resolvedValue: string; // Resolved CSS variable value, or same as value if not a variable
}

export const getMostSpecificValue = (
  values: AtomicValueWithNesting[],
): AtomicValueWithNesting | null => {
  return values.length > 0
    ? values.reduce((min, curr) =>
        curr.nestingLevel < min.nestingLevel ? curr : min,
      )
    : null;
};

export const getMostSpecificValueFromDecomposed = (
  decomposed: Record<string, AtomicValueWithNesting[]>,
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(decomposed)
      .map(([key, values]) => [
        key,
        getMostSpecificValue(values)?.resolvedValue,
      ])
      .filter(([_, value]) => value !== null),
  );
};

const addDefaultNestingLevel = (
  map: Record<string, (value: string) => Record<string, string | null>>,
  defaultNestingLevel: number = 1,
): Record<string, DecomposeMapEntry> => {
  return Object.fromEntries(
    Object.entries(map).map(([key, fn]) => [
      key,
      { fn, nestingLevel: defaultNestingLevel },
    ]),
  );
};

const decomposeMap = {
  ...border.decomposeMap,
  ...addDefaultNestingLevel(spacing.decomposeMap, 1),
  ...addDefaultNestingLevel(text.decomposeMap, 1),
  ...addDefaultNestingLevel(corner.decomposeMap, 1),
} satisfies Record<string, DecomposeMapEntry>;

/**
 * Union type of all supported shorthand property names.
 * This type is automatically derived from the decomposeMap keys.
 * When a new shorthand is added to decomposeMap, this type will include it automatically.
 *
 * Current shorthands:
 * - Border: border, borderTop, borderRight, borderBottom, borderLeft, borderInlineStart, borderInlineEnd, borderWidth, borderStyle, borderColor
 * - Spacing: padding, gap
 * - Corner: borderRadius
 * - Text: font
 */
export type ShorthandPropertyName = Extract<keyof typeof decomposeMap, string>;

/**
 * Type guard to check if a property is a known shorthand.
 */
export const isShorthandProperty = (
  propertyKey: string,
): propertyKey is ShorthandPropertyName => {
  return propertyKey in decomposeMap;
};

export const decompose = (
  cssPropertyType: string,
  value: string,
): Record<string, string | null> | null => {
  const decomposeEntry = decomposeMap[cssPropertyType];

  return decomposeEntry ? decomposeEntry.fn(value) : null;
};

export const decomposeProperties = (
  propertiesValues: Record<string, string>,
): Record<string, string> => {
  let result = {};

  const sortedEntries = Object.entries(propertiesValues).sort(
    ([keyA], [keyB]) => {
      const entryA = decomposeMap[keyA];
      const entryB = decomposeMap[keyB];

      const nestingLevelA = entryA
        ? entryA.nestingLevel
        : Number.MAX_SAFE_INTEGER;
      const nestingLevelB = entryB
        ? entryB.nestingLevel
        : Number.MAX_SAFE_INTEGER;

      return nestingLevelA - nestingLevelB;
    },
  );

  sortedEntries.forEach(([key, value]) => {
    const decomposed = decompose(key, value);

    if (decomposed) {
      result = { ...result, ...decomposed };
    } else {
      result = { ...result, [key]: value };
    }
  });

  if (Object.keys(result).some((key) => decomposeMap[key])) {
    return decomposeProperties(result);
  }

  return Object.fromEntries(
    Object.entries(result).filter(([_, value]) => value !== null),
  ) as Record<string, string>;
};

/**
 * Map of shorthand property names to their placeholder values.
 * Each shorthand needs a specific placeholder value that will decompose into all its atomic children.
 *
 * Placeholder values:
 * - Border properties (border, borderTop, etc.): "1px solid black" → produces width + style + color atomics
 * - borderWidth: "1px" → produces directional width atomics
 * - borderStyle: "solid" → produces directional style atomics
 * - borderColor: "black" → produces directional color atomics
 * - Spacing properties (padding, gap): "0px" → produces directional atomics
 * - Corner properties (borderRadius): "0px" → produces corner atomics
 * - Text properties (font): "16px Arial" → produces font atomics
 */
const SHORTHAND_PLACEHOLDERS: Record<ShorthandPropertyName, string> = {
  // Border and border-directional properties need width + style + color
  border: '1px solid black',
  borderTop: '1px solid black',
  borderRight: '1px solid black',
  borderBottom: '1px solid black',
  borderLeft: '1px solid black',
  borderInlineStart: '1px solid black',
  borderInlineEnd: '1px solid black',

  // Border-specific properties
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'black',

  // Spacing properties
  padding: '0px',
  gap: '0px',

  // Corner properties
  borderRadius: '0px',

  // Text properties
  font: '16px Arial',
};

/**
 * Provides appropriate placeholder values for shorthand properties to ensure complete decomposition.
 * Each shorthand needs a specific placeholder value that will decompose into all its atomic children.
 *
 * @param propertyKey - The shorthand property name
 * @returns Placeholder value string that will decompose into all atomics
 */
export const getPlaceholderForShorthand = (
  propertyKey: ShorthandPropertyName,
): string => {
  return SHORTHAND_PLACEHOLDERS[propertyKey];
};

/**
 * Gets all atomic property keys that a property would decompose into.
 * Uses appropriate placeholder values to ensure complete decomposition.
 * @internal
 */
const getAtomicKeysForProperty = (propertyKey: string): string[] => {
  const decomposeEntry = decomposeMap[propertyKey];

  if (!decomposeEntry) {
    // Property is already atomic
    return [propertyKey];
  }

  // Get the appropriate placeholder for this shorthand
  // Type guard ensures we only call with valid ShorthandPropertyName
  if (!isShorthandProperty(propertyKey)) {
    throw new Error(
      `Property "${propertyKey}" exists in decomposeMap but is not in ShorthandPropertyName type. Please update ShorthandPropertyName.`,
    );
  }

  const placeholderValue = getPlaceholderForShorthand(propertyKey);
  const decomposed = decomposeEntry.fn(placeholderValue);

  const atomics: string[] = [];

  // Get ALL possible atomic keys, even if they decompose to null
  // This is important for eligibility tracking - we need to know all properties
  // a shorthand CAN produce, not just the ones with values in this specific decomposition
  for (const key of Object.keys(decomposed)) {
    // Recursively get atomics for this child property
    atomics.push(...getAtomicKeysForProperty(key));
  }

  return atomics;
};

/**
 * Decomposes properties while considering manifest definitions to enable capability-aware subset detection.
 * Returns simple string values (for comparison/grouping use cases).
 *
 * This is a convenience wrapper around decomposePropertiesWithDefinitionsAndNesting that flattens
 * the nesting level information for use cases that only need simple value comparison.
 *
 * Algorithm:
 * 1. For each property with a value: decompose normally to atomic properties
 * 2. For each defined property WITHOUT a value: decompose to atomic keys and mark each as SYSTEM_EMPTY
 * 3. SYSTEM_EMPTY never overrides real values (real values take precedence)
 * 4. Recursive decomposition: shorthands expand to all their atomic children
 *
 * Purpose:
 * Enables accurate subset detection by comparing both capabilities (definitions) and actual values.
 * Groups with same definitions but no values can now be recognized as subsets of groups with values.
 *
 * @param definitions - Array of property names defined in manifest (e.g., ['border', 'padding', 'color'])
 * @param values - Record of properties with actual values (e.g., {color: 'red'})
 * @returns Record mapping atomic properties to values or SYSTEM_EMPTY symbol
 *
 * @example
 * ```typescript
 * const definitions = ['border', 'color'];
 * const values = {color: 'red'}; // border defined but has no value
 *
 * const result = decomposePropertiesWithDefinitions(definitions, values);
 * // Result: {
 * //   borderTopWidth: SYSTEM_EMPTY,
 * //   borderRightWidth: SYSTEM_EMPTY,
 * //   ...all border atomics as SYSTEM_EMPTY...
 * //   color: 'red'
 * // }
 * ```
 */
export const decomposePropertiesWithDefinitions = (
  definitions: string[],
  values: Record<string, string>,
): Record<string, string | SystemEmpty> => {
  // Use the nesting-aware version and flatten to simple values
  const decomposedWithNesting = decomposePropertiesWithDefinitionsAndNesting(
    definitions,
    values,
  );

  const result: Record<string, string | SystemEmpty> = {};

  for (const [key, atomicValues] of Object.entries(decomposedWithNesting)) {
    if (atomicValues.length === 0) {
      // Empty array means eligible but no value
      result[key] = SYSTEM_EMPTY;
    } else {
      // Take the most specific value (highest nesting level wins)
      const mostSpecific = atomicValues.reduce((prev, current) =>
        current.nestingLevel > prev.nestingLevel ? current : prev,
      );
      result[key] = mostSpecific.value;
    }
  }

  return result;
};

/** Custom decompose function type - allows wrapping decompose with mitigation logic. */
export type DecomposeFn = (
  propertyType: string,
  value: string,
) => Record<string, string | null> | null;

/**
 * Core decomposition function with eligibility tracking and nesting level information.
 * This is the single source of truth for decomposition logic.
 *
 * Features:
 * - Decomposes shorthands to atomics with nesting level tracking
 * - Marks eligible properties without values as empty arrays
 * - Handles both CSS properties and custom properties (via getType mapper)
 * - Supports multiple values at different specificity levels
 *
 * Used by:
 * - CSSPropertiesEngine (direct mode)
 * - CSSCustomPropertiesEngine (mapped mode with getType)
 * - decomposePropertiesWithDefinitions (wrapper that flattens nesting info)
 *
 * @param definitions - Array of property types defined in manifest
 * @param values - Record of properties with actual values
 * @param getType - Optional function to get the type for each value key (for custom properties)
 * @returns Record mapping atomic properties to arrays of values with nesting levels, or empty arrays for eligible properties without values
 *
 * @example Direct mode (CSS properties)
 * ```typescript
 * const definitions = ['border', 'color'];
 * const values = {color: 'red'}; // border defined but has no value
 *
 * const result = decomposePropertiesWithDefinitionsAndNesting(definitions, values);
 * // Result: {
 * //   borderTopWidth: [],  // eligible but no value
 * //   borderRightWidth: [], // eligible but no value
 * //   ...all border atomics as []...
 * //   color: [{ value: 'red', nestingLevel: 0 }]
 * // }
 * ```
 *
 * @example Mapped mode (custom properties)
 * ```typescript
 * const definitions = ['border', 'color'];
 * const values = {'--border-theme': '1px solid red', '--text-color': 'blue'};
 * const getType = (key) => metadata[key].cssPropertyType;
 *
 * const result = decomposePropertiesWithDefinitionsAndNesting(definitions, values, getType);
 * // Result: {
 * //   borderTopWidth: [{ value: '1px', nestingLevel: 2 }],
 * //   borderTopStyle: [{ value: 'solid', nestingLevel: 2 }],
 * //   ...
 * //   color: [{ value: 'blue', nestingLevel: 0 }]
 * // }
 * ```
 */
export function decomposePropertiesWithDefinitionsAndNesting(
  definitions: string[] | CssPropertyEnumCssPropertyType[],
  values: Record<string, string>,
  getType?: (key: string) => string,
  decomposeFn: DecomposeFn = decompose,
): Record<string, AtomicValueWithNesting[]> {
  const result: Record<string, AtomicValueWithNesting[]> = {};

  if (getType) {
    for (const [key, value] of Object.entries(values)) {
      const type = getType(key);
      const singlePropDecomposed = decomposePropertiesWithNesting(
        { [type]: value },
        decomposeFn,
      );
      for (const [atomicKey, atomicValues] of Object.entries(
        singlePropDecomposed,
      )) {
        if (!result[atomicKey]) {
          result[atomicKey] = [];
        }
        result[atomicKey].push(...atomicValues);
      }
    }
  } else {
    const decomposedValues = decomposePropertiesWithNesting(
      values,
      decomposeFn,
    );
    Object.assign(result, decomposedValues);
  }

  // Add eligibility markers for properties without values (empty arrays)
  for (const definition of definitions) {
    // Get all atomic keys this definition would produce
    const atomicKeys = getAtomicKeysForProperty(definition);

    // Mark each atomic with empty array if it doesn't have a real value
    for (const atomicKey of atomicKeys) {
      if (result[atomicKey] === undefined) {
        result[atomicKey] = [];
      }
      // If result[atomicKey] already exists with values, don't override
    }
  }

  return result;
}

/**
 * Decomposes properties to lowest atomics while tracking nesting levels.
 * Supports multiple values at the same atomic with different specificity levels.
 *
 * @param propertiesValues - Properties to decompose
 * @param decomposeFn - Decompose function to use (allows wrapping with mitigation)
 * @returns Record of atomic properties to arrays of values with nesting levels
 */
export const decomposePropertiesWithNesting = (
  propertiesValues: Record<string, string>,
  decomposeFn: DecomposeFn = decompose,
): Record<string, AtomicValueWithNesting[]> => {
  const result: Record<string, AtomicValueWithNesting[]> = {};

  const decomposeRecursively = (
    key: string,
    value: string,
    currentNestingLevel: number,
  ): void => {
    const decomposed = decomposeFn(key, value);

    if (decomposed) {
      const nextNestingLevel = currentNestingLevel + 1;
      for (const [childKey, childValue] of Object.entries(decomposed)) {
        if (childValue !== null) {
          decomposeRecursively(childKey, childValue, nextNestingLevel);
        }
      }
    } else {
      mergeAtomicValue(key, value, currentNestingLevel);
    }
  };

  /**
   * Merges an atomic value using specificity rules.
   * @param atomic - Atomic property name
   * @param value - Value to add
   * @param nestingLevel - Nesting level (0 = most specific)
   */
  const mergeAtomicValue = (
    atomic: string,
    value: string,
    nestingLevel: number,
  ): void => {
    if (!result[atomic]) {
      // Rule 1: No existing values - create array with single value
      // resolvedValue is initially set to value (will be updated later with actual resolution)
      result[atomic] = [{ value, nestingLevel, resolvedValue: value }];
      return;
    }

    const existing = result[atomic];

    // Rule 2: Check if more specific value exists (lower nestingLevel)
    const hasMoreSpecific = existing.some(
      (entry) => entry.nestingLevel < nestingLevel,
    );
    if (hasMoreSpecific) {
      // Skip - more specific value takes precedence
      return;
    }

    // Rule 3: Filter out less specific values (higher nestingLevel)
    // Keep values that are equally or more specific (<=)
    const filtered = existing.filter(
      (entry) => entry.nestingLevel <= nestingLevel,
    );

    // Rule 4: Add current value (avoiding duplicates at same level)
    const isDuplicate = filtered.some(
      (entry) => entry.nestingLevel === nestingLevel && entry.value === value,
    );

    if (!isDuplicate) {
      // resolvedValue is initially set to value (will be updated later with actual resolution)
      filtered.push({ value, nestingLevel, resolvedValue: value });
    }

    result[atomic] = filtered;
  };

  // Process each property starting at nesting level 0
  for (const [key, value] of Object.entries(propertiesValues)) {
    decomposeRecursively(key, value, 0);
  }

  return result;
};
